import { ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';
import React, { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import sadCatImg from '@/shared/assets/images/sad-cat.png';
import { NEW_LINE, SliceName } from '@/shared/config';
import { Input, Result, WidgetCard } from '@/shared/ui';

import {
  DoctorInterface,
  useGetOrganizationDoctorsInterfaces,
} from '@/entities/organization';

import { TableWithInfiniteScroll } from '@/widgets/TableWithInfiniteScroll';

import { useGetColumnsScheme, useStaffMFARequirement } from '../../hooks';
import { RemoveStaffMemberModal } from '../RemoveStaffMember/RemoveStaffMember';
import { RequireMFAModal } from '../RequireMFAModal';

import styles from './StaffMembers.module.scss';

type StaffMembersProps = {
  className?: string;
};

export const StaffMembers: FC<StaffMembersProps> = (props) => {
  const { className } = props;

  const [removeModalOpened, setRemoveModalOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [deletingUserId, setDeletingUserId] = useState('');

  const { formatMessage } = useIntl();

  const companyDoctors = useGetOrganizationDoctorsInterfaces();

  const {
    organizationName,
    canEdit: canEditMFA,
    isMFASettingsEnabled,
    isRequiredForEveryone,
    isConfirmationOpen,
    isSaving,
    requestOrganizationRequirement,
    confirmOrganizationRequirement,
    cancelOrganizationRequirement,
    setStaffRequirement,
  } = useStaffMFARequirement();

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  const handleOpenCloseRemoveModal = () => {
    setRemoveModalOpened(!removeModalOpened);
  };

  const handleOpenRemoveModal = (userId: string) => {
    setDeletingUserId(userId);
    setRemoveModalOpened(!removeModalOpened);
  };

  const columnsScheme = useGetColumnsScheme({
    canEditMFA,
    isMFARequiredForEveryone: isRequiredForEveryone,
    isMFASettingsEnabled,
    onRemoveMember: handleOpenRemoveModal,
    onToggleOrganizationMFA: requestOrganizationRequirement,
    onToggleStaffMFA: setStaffRequirement,
  });

  const doctors = useMemo(() => {
    return (
      companyDoctors?.filter(
        (doc) =>
          doc.fullName.toLowerCase().includes(search.toLowerCase()) ||
          doc.specializations
            .join()
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          doc.roles.join().toLowerCase().includes(search.toLowerCase()),
      ) || []
    );
  }, [search, companyDoctors]);

  const showNothingFound = !doctors.length && Boolean(search);

  const activeMembersCount = companyDoctors.filter(
    (doctor) => !doctor.isDeleted,
  ).length;

  return (
    <div
      className={cn(className, styles.container)}
      data-component="staff-members"
    >
      <Input
        className={styles.search}
        type="search"
        name="search-staff"
        value={search}
        placeholder={formatMessage({
          id: 'companySettings.searchPlaceholder',
          defaultMessage: 'Search by name, job position, or access level',
        })}
        icon="search"
        onChange={handleSearch}
      />
      <WidgetCard className={styles.widgetCard}>
        <TableWithInfiniteScroll
          listData={doctors}
          reFetchList={() => {}}
          itemCounter={100}
          columnsScheme={columnsScheme as ColumnDef<DoctorInterface>[]}
          className={styles.table}
          loadingState={SliceName.access}
          showEmpty={showNothingFound}
          emptyComponent={
            <Result
              className={styles.emptySearchResult}
              icon={
                <img
                  src={sadCatImg}
                  width={130}
                  height={130}
                  alt={formatMessage({
                    id: 'imgAltText.sadCat',
                    defaultMessage: 'Sad cat',
                  })}
                />
              }
              text={formatMessage(
                {
                  id: 'companySettings.emptySearchResult',
                  defaultMessage:
                    'It looks like there are no team members with these search parameters.{newLine}Try to change search or filtering parameters.',
                },
                { newLine: NEW_LINE },
              )}
            />
          }
        />

        <RemoveStaffMemberModal
          isOpen={removeModalOpened}
          onCancel={handleOpenCloseRemoveModal}
          deletingUserId={deletingUserId}
        />

        <RequireMFAModal
          isOpen={isMFASettingsEnabled && isConfirmationOpen}
          isSaving={isSaving}
          organizationName={organizationName}
          membersCount={activeMembersCount}
          onCancel={cancelOrganizationRequirement}
          onConfirm={confirmOrganizationRequirement}
        />
      </WidgetCard>
    </div>
  );
};
